import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../utils/api';
import { Mail, Loader2, Save, Info, Eye, EyeOff, Send, X, AlertCircle } from 'lucide-react';

interface EmailTemplateDto {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  updatedAt: string;
}

const PIERCING_PLACEHOLDERS = [
  '{{clientFirstName}}',
  '{{clientLastName}}',
  '{{clientEmail}}',
  '{{appointmentDate}}',
  '{{appointmentTime}}',
  '{{appointmentDateFormatted}}',
  '{{shopName}}',
  '{{shopAddress}}',
  '{{piercingList}}',
  '{{totalPrice}}',
  '{{depositAmount}}',
  '{{currency}}',
  '{{notes}}',
  '{{year}}',
  '{{cancelUrl}}',
  '{{cancellationPolicyText}}',
  '{{cancelDeadlineFormatted}}',
  '{{cancellationReason}}',
  '{{serviceName}}',
];

const PREVIEW_VALUES: Record<string, string> = {
  clientFirstName: 'Camille',
  clientLastName: 'Martin',
  clientEmail: 'camille.martin@example.com',
  appointmentDate: '15/10/2026',
  appointmentTime: '14:30',
  appointmentDateFormatted: 'jeudi 15 octobre 2026 à 14h30',
  shopName: 'Abraxas Saint-Honoré',
  shopAddress: '10 rue Saint-Honoré, 75001 Paris',
  piercingList: 'Hélix – Titane',
  totalPrice: '80,00',
  depositAmount: '30,00',
  depositStatus: 'Payé',
  currency: '€',
  notes: 'Merci de vous présenter 10 minutes avant le rendez-vous.',
  year: String(new Date().getFullYear()),
  cancelUrl: '#',
  cancellationPolicyText: 'Annulation possible jusqu’à 48 heures avant le rendez-vous.',
  cancelDeadlineFormatted: '13 octobre 2026 à 14h30',
  cancellationReason: 'Empêchement personnel',
  serviceName: 'Séance de tatouage',
  practitionerName: 'Alex Dupont',
  tattooStyle: 'Fineline',
  tattooPlacement: 'Avant-bras',
  tattooSize: '10 × 6 cm',
  checkInCode: '482917',
};

function applyPreviewValues(value: string): string {
  return value.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (placeholder, key: string) =>
    Object.prototype.hasOwnProperty.call(PREVIEW_VALUES, key)
      ? PREVIEW_VALUES[key]
      : placeholder
  );
}

export function EmailTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<EmailTemplateDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testError, setTestError] = useState('');

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('/api/admin/email-templates'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load templates');
      const data = await res.json();
      if (data.success && data.data) setTemplates(data.data);
    } catch (e) {
      setError(t('emailTemplates.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id: string) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`/api/admin/email-templates/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load template');
      const data = await res.json();
      if (data.success && data.data) {
        setSelectedId(id);
        setSubject(data.data.subject);
        setBodyHtml(data.data.bodyHtml);
      }
    } catch (e) {
      setError(t('emailTemplates.errors.loadFailed'));
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (templates.length > 0 && !selectedId) {
      const piercing = templates.find(t => t.id === 'piercing_booking_confirmation');
      loadOne(piercing ? piercing.id : templates[0].id);
    }
  }, [templates]);

  const handleSave = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`/api/admin/email-templates/${selectedId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subject, bodyHtml })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
      setSuccess(t('emailTemplates.success.saved'));
      await loadTemplates();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('emailTemplates.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = () => {
    setTestError('');
    setTestRecipient('');
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!selectedId) return;
    const recipient = testRecipient.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      setTestError(t('emailTemplates.test.invalidEmail'));
      return;
    }

    try {
      setSendingTest(true);
      setTestError('');
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`/api/admin/email-templates/${selectedId}/test`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: recipient, subject, bodyHtml }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || t('emailTemplates.test.sendFailed'));
      }
      setTestDialogOpen(false);
      setSuccess(t('emailTemplates.test.sent', { email: recipient }));
    } catch (e) {
      setTestError(e instanceof Error ? e.message : t('emailTemplates.test.sendFailed'));
    } finally {
      setSendingTest(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedId);
  const isPiercingTemplate =
    selectedId === 'piercing_booking_confirmation' ||
    selectedId === 'piercing_booking_confirmation_minor';
  const previewSubject = useMemo(() => applyPreviewValues(subject), [subject]);
  const previewHtml = useMemo(() => applyPreviewValues(bodyHtml), [bodyHtml]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-3">
          <Mail className="w-8 h-8 text-primary-600" />
          <span>{t('emailTemplates.title')}</span>
        </h1>
        <p className="text-textSecondary mt-2">{t('emailTemplates.subtitle')}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">{success}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('emailTemplates.list')}</h2>
          <ul className="space-y-1">
            {templates.map(t => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => loadOne(t.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                    selectedId === t.id ? 'bg-primary-100 text-primary-800 font-medium' : 'hover:bg-gray-100'
                  }`}
                >
                  {t.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {selectedTemplate && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('emailTemplates.subject')}</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Votre réservation piercing est confirmée – {{shopName}}"
                />
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <label className="block text-sm font-medium text-gray-700">{t('emailTemplates.bodyHtml')}</label>
                  <button
                    type="button"
                    onClick={() => setShowPreview(current => !current)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {showPreview ? (
                      <EyeOff className="mr-2 h-4 w-4" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    {showPreview ? t('emailTemplates.hidePreview') : t('emailTemplates.showPreview')}
                  </button>
                </div>

                <div className={showPreview ? 'grid grid-cols-1 xl:grid-cols-2 gap-4' : ''}>
                  <div>
                    <textarea
                      value={bodyHtml}
                      onChange={e => setBodyHtml(e.target.value)}
                      rows={24}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-primary-500 focus:border-primary-500"
                      placeholder="<html>..."
                    />
                  </div>

                  {showPreview && (
                    <div className="min-w-0">
                      <div className="rounded-t-md border border-b-0 border-gray-300 bg-gray-50 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              {t('emailTemplates.preview')}
                            </p>
                            <p className="mt-1 break-words text-sm font-semibold text-gray-900">
                              {previewSubject}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={openTestDialog}
                            className="inline-flex shrink-0 items-center rounded-md border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
                          >
                            <Send className="mr-2 h-4 w-4" />
                            {t('emailTemplates.test.button')}
                          </button>
                        </div>
                      </div>
                      <iframe
                        title={t('emailTemplates.preview')}
                        srcDoc={previewHtml}
                        sandbox=""
                        className="h-[620px] w-full rounded-b-md border border-gray-300 bg-white"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        {t('emailTemplates.previewHelp')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {isPiercingTemplate && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">{t('emailTemplates.placeholdersTitle')}</p>
                    <p className="mb-2">{t('emailTemplates.placeholdersHelp')}</p>
                    <ul className="flex flex-wrap gap-2">
                      {PIERCING_PLACEHOLDERS.map(ph => (
                        <li key={ph} className="bg-white px-2 py-1 rounded border border-blue-200 font-mono text-xs">
                          {ph}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('emailTemplates.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('emailTemplates.save')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {testDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-template-test-title"
            className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl"
          >
            <div className="bg-gradient-to-br from-primary-500 to-accent-600 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 id="email-template-test-title" className="text-lg font-semibold">
                      {t('emailTemplates.test.title')}
                    </h3>
                    <p className="mt-0.5 text-sm text-white/90">{selectedTemplate?.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTestDialogOpen(false)}
                  disabled={sendingTest}
                  className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50"
                  aria-label={t('emailTemplates.test.cancel')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form
              className="px-6 py-5"
              onSubmit={(event) => {
                event.preventDefault();
                void handleSendTest();
              }}
            >
              <p className="text-sm leading-6 text-gray-600">{t('emailTemplates.test.description')}</p>
              <label htmlFor="email-template-test-recipient" className="mt-4 block text-sm font-medium text-gray-700">
                {t('emailTemplates.test.recipient')}
              </label>
              <input
                id="email-template-test-recipient"
                type="email"
                autoFocus
                required
                value={testRecipient}
                onChange={(event) => setTestRecipient(event.target.value)}
                placeholder={t('emailTemplates.test.recipientPlaceholder')}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
              />
              {testError && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{testError}</span>
                </div>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setTestDialogOpen(false)}
                  disabled={sendingTest}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('emailTemplates.test.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={sendingTest}
                  className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('emailTemplates.test.sending')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {t('emailTemplates.test.send')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
